import { Button } from 'components/ui/button';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useResetTotalsMutation } from 'store/api/invoices';
import { InvoiceData, InvoiceDetailsTotals, InvoiceTotalsForm } from 'types/invoices';
import { t } from 'utils/getTranslations';
import InvoiceExpenseTotalsTableItem from './InvoiceExpenseTotalsTableItem';
import InvoiceTotalsTableHeader from './InvoiceTotalsTableHeader';
import { TotalsTableCard, totalsButtonStyle } from './TotalsTableCard';

interface Props {
    invoiceTotals?: InvoiceDetailsTotals;
    invoice: InvoiceData;
    isInViewMode: boolean;
    index: number;
    matterId: number;
    paymentCurrency: string;
}

const InvoiceExpenseTotals = ({ invoiceTotals, invoice, isInViewMode, index, matterId, paymentCurrency }: Props) => {
    const { control } = useFormContext<InvoiceTotalsForm>();
    const { fields } = useFieldArray({
        control,
        name: `invoiceDetailsTotals.${index}.invoiceExpenseTotals`,
        keyName: 'formKey',
    });

    const [resetTotals] = useResetTotalsMutation();
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    // Reverting the rows would change a shared-payment copy's amounts, which are the group's.
    const isSplitInvoice = (invoice.isSplit ?? false) && !isSharedPaymentCopy;

    const tableContent = useMemo(
        () =>
            fields.map((field, index) => {
                return (
                    <InvoiceExpenseTotalsTableItem
                        key={field.formKey}
                        invoiceTotals={invoiceTotals?.invoiceExpenseTotals?.[index] || undefined}
                        isInViewMode={isInViewMode}
                        invoice={invoice}
                        matterId={matterId}
                    />
                );
            }),
        [fields, invoiceTotals?.invoiceExpenseTotals, isInViewMode, invoice, matterId]
    );

    return (
        <TotalsTableCard
            title={<FormattedMessage id="invoices.totals-tab.expenses" />}
            isEmpty={!fields.length}
            actions={
                invoiceTotals && !isInViewMode && isSplitInvoice ? (
                    <Button
                        {...totalsButtonStyle('w-[125px]')}
                        onClick={() => {
                            void resetTotals({
                                invoiceId: invoice.id,
                                matterId: invoiceTotals.matterId,
                                sourceType: 'EXPENSE',
                            });
                        }}
                    >
                        {t('invoices.totals-tab.revert-changes')}
                    </Button>
                ) : null
            }
        >
            <InvoiceTotalsTableHeader paymentCurrency={paymentCurrency} />
            <div className="flex flex-col">{tableContent}</div>
        </TotalsTableCard>
    );
};

export default InvoiceExpenseTotals;
